import type { Company } from "@/apis/gql/types";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { CompanyRole } from "@/lib/miscellaneous/authorization";
import { getUserAccountingFirmRole } from "@/lib/users/user-roles";
import type { TranslationKey } from "@/localisation/types";
import { USER_COMPANIES_QUERY } from "@/queries/companies/get-all";
import { useUpdateCompanyAccessUsers } from "@/queries/companies/users/update-user-accesses";
import { useUsers } from "@/queries/users/get-all";

import { User, UserAccountingFirm } from "@/types/user";
import { Shield, User as UserIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

interface EditAccessModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  company: Company;
}

export function EditAccessModal({
  open,
  onOpenChange,
  company,
}: EditAccessModalProps) {
  const { t } = useTranslation();
  const { users: allUsers, loading: usersLoading } = useUsers(!open, false);
  const [search, setSearch] = useState("");
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [updateCompanyAccessUsers] = useUpdateCompanyAccessUsers([
    { query: USER_COMPANIES_QUERY },
  ]);
  const companyUserIds = (company.users || []).map((u) => u.userId);

  // Initialize selectedUserIds from company.users when modal opens or company changes
  useEffect(() => {
    setSelectedUserIds(companyUserIds);
  }, [company.companyId, open]);

  const filteredUsers = useMemo(() => {
    return allUsers.filter(
      (user) =>
        user.name.toLowerCase().includes(search.toLowerCase()) ||
        user.email.toLowerCase().includes(search.toLowerCase()) ||
        (getUserAccountingFirmRole(user) &&
          (
            t(
              `role.${getUserAccountingFirmRole(user)}` as TranslationKey,
            ) as string
          )
            .toLowerCase()
            .includes(search.toLowerCase())),
    );
  }, [allUsers, search, t]);

  const handleToggleUser = (userId: string) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId],
    );
  };

  const handleSave = async () => {
    try {
      const usersToAdd = selectedUserIds.filter(
        (userId) => !companyUserIds.includes(userId),
      );
      const usersToRemove = companyUserIds.filter(
        (userId) => !selectedUserIds.includes(userId),
      );

      const companyAssignments = usersToAdd.map((userId) => ({
        userId,
        companyId: company.companyId,
        role: CompanyRole.EDITOR,
      }));

      const assignmentsToRemove = usersToRemove.map((userId) => ({
        userId,
        companyId: company.companyId,
      }));

      await updateCompanyAccessUsers(companyAssignments, assignmentsToRemove);
      onOpenChange(false);
    } catch (err) {
      console.error(
        "[EditAccessModal] Error updating company access users",
        err,
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl w-full">
        <DialogHeader>
          <DialogTitle>
            {t("edit_access_title", { company: company.companyName })}
          </DialogTitle>
        </DialogHeader>
        <div className="mb-4">
          <Input
            placeholder={t("search_user_placeholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="border rounded-md divide-y h-80 overflow-y-auto mb-4">
          {/* Header row */}
          <div className="flex items-center px-4 py-2 gap-4 bg-gray-50 font-medium text-base">
            <Checkbox
              checked={
                filteredUsers.length > 0 &&
                filteredUsers.every((user: any) =>
                  selectedUserIds.includes(user.userId),
                )
              }
              onCheckedChange={(checked) => {
                if (checked) {
                  setSelectedUserIds(
                    filteredUsers.map((user: any) => user.userId),
                  );
                } else {
                  setSelectedUserIds([]);
                }
              }}
            />
            <div className="flex-1">{t("users.name")}</div>
            <div className="w-32">{t("users.role")}</div>
          </div>

          {usersLoading ? (
            <div className="p-4 text-center text-gray-500">{t("loading")}</div>
          ) : filteredUsers.length > 0 ? (
            filteredUsers.map((user) => (
              <div
                key={user.userId}
                className="flex items-center px-4 py-2 gap-4"
              >
                <Checkbox
                  checked={selectedUserIds.includes(user.userId)}
                  onCheckedChange={() => handleToggleUser(user.userId)}
                />
                <div className="flex-1">
                  <div>{user.name}</div>
                  <div className="text-sm text-gray-500">{user.email}</div>
                </div>
                <div className="w-32 text-sm flex items-center gap-2">
                  {getUserAccountingFirmRole(user) === "admin" ? (
                    <>
                      <Shield className="h-4 w-4" />
                      {t("role.admin")}
                    </>
                  ) : (
                    <>
                      <UserIcon className="h-4 w-4" />
                      {t("role.user")}
                    </>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="p-4 text-center text-gray-500">
              {t("no_users_found")}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("cancel")}
          </Button>
          <Button onClick={handleSave}>{t("save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default EditAccessModal;
